import { useEffect, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Havayolu logosu — legacy MVC'nin `src="/Content/img/logos/@(Model.MarkettingAirline).png"`
 * karşılığı. Dosyalar `public/img/airlines/<KOD>.png` (büyük harf 2-harfli IATA kodu, legacy
 * setinden kopyalandı). API `FlightSegment.MarkettingAirline` alanını `api/client.ts`
 * interceptor'ı camelCase'e çevirdiği için çağıranlar `segment.markettingAirline`'ı geçer;
 * `airlineCodeOf` yaygın fallback zincirini (pazarlayan → operasyon → uçuş no ön eki) kapsar.
 *
 * Logo dosyası yoksa (`onError`) kırık ikon yerine 2-harfli kod monogramına düşer; monogram
 * da istenmiyorsa `showFallback={false}` ile hiçbir şey render edilmez (ör. filtre listesi).
 */
export function airlineCodeOf(seg: {
  markettingAirline?: string;
  operatingAirline?: string;
  flightNumber?: string;
}): string | undefined {
  const raw = seg.markettingAirline || seg.operatingAirline || seg.flightNumber?.slice(0, 2) || '';
  return raw.trim().toUpperCase() || undefined;
}

export function airlineNameOf(seg: {
  markettingAirlineName?: string;
  operatingAirlineName?: string;
}): string | undefined {
  return seg.markettingAirlineName || seg.operatingAirlineName || undefined;
}

type AirlineLogoProps = {
  code?: string | null;
  name?: string | null;
  /** Kenar uzunluğu (px). Varsayılan 24. */
  size?: number;
  /** Logo bulunamazsa 2-harfli kod monogramı göster (varsayılan) veya hiçbir şey. */
  showFallback?: boolean;
};

export function AirlineLogo({ code, name, size = 24, showFallback = true }: AirlineLogoProps) {
  const normalized = code?.trim().toUpperCase() || '';
  const [failed, setFailed] = useState(false);

  // Aynı bileşen farklı kodlarla yeniden kullanılırsa (liste) hata durumunu sıfırla.
  useEffect(() => setFailed(false), [normalized]);

  const label = name || normalized || undefined;

  if (!normalized || failed) {
    if (!showFallback || !normalized) return null;
    return (
      <Box
        aria-label={label}
        title={label}
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: 0.5,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: Math.round(size * 0.42),
          fontWeight: 600,
          lineHeight: 1,
          color: 'text.secondary',
          bgcolor: 'action.hover',
        }}
      >
        {normalized}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={`${process.env.PUBLIC_URL}/img/airlines/${normalized}.png`}
      alt={label ?? ''}
      title={label}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{ width: size, height: size, objectFit: 'contain', flexShrink: 0, borderRadius: 0.5 }}
    />
  );
}
